import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconPlus, IconX } from "@tabler/icons-react";
import {
  addContactTag,
  removeContactTag,
} from "../services/contacts.service";
import {
  searchTags,
  getTagsByPipeline,
} from "../../../../screens/Tags/services/TagServices";

/**
 * Tags inline pra um contato: mostra chips com X pra remover + botão
 * "+ Tag" que abre dropdown de busca/seleção.
 *
 * Sugestões priorizam tags preset do pipeline do contato: quando
 * `pipelineId` é passado, a busca inclui `?pipeline=<id>` que retorna
 * account-wide + presets desse pipeline. Criação de tags fica restrita
 * ao gerenciamento do pipeline (/crm → Gerenciar pipelines).
 */
const ContactTagsInline = ({ contact, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  const contactId = contact?.id;
  const pipelineId = contact?.pipeline?.id || contact?.pipeline || null;
  const currentTags = contact?.contact_tags_data || [];
  const currentTagIds = currentTags.map((t) => t.id);

  // Click fora fecha o dropdown
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Carrega sugestões iniciais quando abre (tags do pipeline)
  useEffect(() => {
    if (!open || query.length >= 2) return;
    const load = async () => {
      setLoading(true);
      try {
        if (pipelineId) {
          const data = await getTagsByPipeline(pipelineId);
          const all = data?.results || data || [];
          setSuggestions(all.filter((t) => !currentTagIds.includes(t.id)));
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, pipelineId]);

  // Busca textual quando digita
  useEffect(() => {
    if (!open || query.length < 2) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchTags(query, null, 10, pipelineId);
        const results = response?.results || response || [];
        setSuggestions(results.filter((t) => !currentTagIds.includes(t.id)));
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, open, pipelineId]);

  const handleAdd = useCallback(async (tag) => {
    if (!contactId || saving) return;
    setSaving(true);
    try {
      await addContactTag(contactId, tag.id);
      onChange?.({
        ...contact,
        contact_tags_data: [...currentTags, tag],
      });
      setQuery("");
    } catch (err) {
      // erro silencioso, próximo retry
    } finally {
      setSaving(false);
    }
  }, [contactId, contact, currentTags, onChange, saving]);

  const handleRemove = useCallback(async (tag) => {
    if (!contactId || saving) return;
    setSaving(true);
    try {
      await removeContactTag(contactId, tag.id);
      onChange?.({
        ...contact,
        contact_tags_data: currentTags.filter((t) => t.id !== tag.id),
      });
    } catch (err) {
      // silencioso
    } finally {
      setSaving(false);
    }
  }, [contactId, contact, currentTags, onChange, saving]);

  return (
    <div ref={containerRef} style={{ marginTop: 8, position: "relative" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {currentTags.map((tag) => (
          <span
            key={tag.id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 8px",
              borderRadius: 12,
              background: tag.color || "#64748b",
              color: "#fff",
              fontSize: 11,
              fontWeight: 500,
            }}
          >
            {tag.name}
            <button
              onClick={() => handleRemove(tag)}
              disabled={saving}
              title="Remover tag"
              style={{
                background: "rgba(255,255,255,0.25)",
                border: "none",
                borderRadius: "50%",
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#fff",
                padding: 0,
              }}
            >
              <IconX size={9} />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={saving}
          title="Adicionar tag"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            padding: "3px 8px",
            borderRadius: 12,
            background: "transparent",
            border: "1px dashed #94a3b8",
            color: "#475569",
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          <IconPlus size={10} /> Tag
        </button>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#fff",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
            zIndex: 100,
            maxHeight: 220,
            overflowY: "auto",
            padding: 6,
          }}
        >
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setOpen(false);
                setQuery("");
              }
            }}
            placeholder="Buscar tag..."
            disabled={saving}
            style={{
              width: "100%",
              padding: "5px 8px",
              border: "1px solid #cbd5e1",
              borderRadius: 4,
              fontSize: 12,
              marginBottom: 6,
            }}
          />

          {loading && (
            <div style={{ fontSize: 11, color: "#94a3b8", padding: 4 }}>Buscando...</div>
          )}

          {!loading && suggestions.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {suggestions.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleAdd(tag)}
                  disabled={saving}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 6px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: tag.color || "#64748b",
                    }}
                  />
                  {tag.name}
                  {tag.pipeline_ids?.includes(pipelineId) && (
                    <span style={{ fontSize: 10, color: "#64748b", marginLeft: "auto" }}>
                      preset
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {!loading && suggestions.length === 0 && query.length < 2 && pipelineId && (
            <div style={{ fontSize: 11, color: "#94a3b8", padding: 4 }}>
              Sem tags preset neste pipeline.
            </div>
          )}

          {!loading && suggestions.length === 0 && query.length >= 2 && (
            <div style={{ fontSize: 11, color: "#94a3b8", padding: 4 }}>
              Nenhuma tag encontrada.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactTagsInline;
